import { el, clear } from "../../utils/dom";
import { getState, setState } from "../../state/store";
import { uniqueCI } from "../../utils/array";
import { nid } from "../../utils/id";
import { createMultiSelectDropdown } from "./MultiSelectDropdown";
import { toast } from "./Toasts";
import { createAvatarElement, processImageFile } from "../../utils/avatar";

export function openRosterDialog() {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    // Create modal backdrop
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 900px; width: 90%; max-height: 85vh; display: flex; flex-direction: column; border: 1px solid var(--border);"
    });

    const title = el("h2", { style: "margin: 0 0 20px 0;" }, "Manage Roster");

    // Import section
    const importTA = el("textarea", {
        rows: 4,
        placeholder: "Paste names here (one per line or tab-separated)...",
        style: "width: 100%; margin-bottom: 12px; resize: vertical;"
    }) as HTMLTextAreaElement;

    const importBtn = el("button", { class: "primary", style: "margin-bottom: 20px;" }, "Import / Merge Students");
    importBtn.addEventListener("click", () => {
        const raw = importTA.value;
        const parts = raw.split(/\r?\n|\t/).map(s => s.trim()).filter(Boolean);
        if (!parts.length) return;

        const canonical = uniqueCI(parts);
        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === currentState.activeClassId);
        if (!currentCls) return;

        setState(ns => {
            const c = ns.classes.find(x => x.id === currentCls.id)!;
            const existingLower = new Set(c.students.map(s => s.name.toLowerCase()));
            for (const name of canonical) {
                if (existingLower.has(name.toLowerCase())) continue;
                c.students.push({ id: nid("s_"), name, skill: 5, absent: false, tags: [] });
            }
        });

        importTA.value = "";
        renderStudentList();
    });

    // Search input
    let searchTerm = "";

    const searchInput = el("input", {
        type: "text",
        placeholder: "Search students by name...",
        style: "width: 100%; padding: 8px; margin-bottom: 12px;"
    }) as HTMLInputElement;

    searchInput.addEventListener("input", () => {
        searchTerm = searchInput.value.toLowerCase().trim();
        renderStudentList();
    });

    // Track selected students for bulk operations
    const selectedStudentIds = new Set<string>();

    // Bulk actions toolbar (hidden when no selection)
    const bulkActionsBar = el("div", {
        style: "display: none; padding: 12px; background: var(--primary); border-radius: 8px; margin-bottom: 12px; gap: 8px; align-items: center; flex-wrap: wrap;"
    });

    const selectionCountSpan = el("span", {
        style: "color: white; font-weight: 500; margin-right: 12px;"
    }, "0 selected");

    // Bulk tag add dropdown
    const bulkAddTagSelect = el("select", {
        style: "padding: 6px 10px; border-radius: 6px; border: none;"
    }) as HTMLSelectElement;

    const bulkAddTagBtn = el("button", {
        style: "padding: 6px 12px; background: white; border: none; border-radius: 6px; cursor: pointer;"
    }, "+ Add Tag");

    // Bulk tag remove dropdown
    const bulkRemoveTagSelect = el("select", {
        style: "padding: 6px 10px; border-radius: 6px; border: none;"
    }) as HTMLSelectElement;

    const bulkRemoveTagBtn = el("button", {
        style: "padding: 6px 12px; background: white; border: none; border-radius: 6px; cursor: pointer;"
    }, "- Remove Tag");

    // Bulk skill input
    const bulkSkillInput = el("input", {
        type: "number",
        min: "0",
        max: "10",
        value: "5",
        style: "width: 60px; padding: 6px; border-radius: 6px; border: none;"
    }) as HTMLInputElement;

    const bulkSetSkillBtn = el("button", {
        style: "padding: 6px 12px; background: white; border: none; border-radius: 6px; cursor: pointer;"
    }, "Set Skill");

    // Deselect all button
    const deselectAllBtn = el("button", {
        style: "padding: 6px 12px; background: rgba(255,255,255,0.2); color: white; border: none; border-radius: 6px; cursor: pointer; margin-left: auto;"
    }, "Deselect All");

    bulkActionsBar.append(
        selectionCountSpan,
        bulkAddTagSelect,
        bulkAddTagBtn,
        bulkRemoveTagSelect,
        bulkRemoveTagBtn,
        el("span", { style: "color: rgba(255,255,255,0.7);" }, "|"),
        el("span", { style: "color: white; font-size: 12px;" }, "Skill:"),
        bulkSkillInput,
        bulkSetSkillBtn,
        deselectAllBtn
    );

    function updateBulkActionsBar() {
        const count = selectedStudentIds.size;
        bulkActionsBar.style.display = count > 0 ? "flex" : "none";
        selectionCountSpan.textContent = `${count} selected`;

        // Update tag dropdowns
        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === currentState.activeClassId);
        if (!currentCls) return;

        clear(bulkAddTagSelect);
        clear(bulkRemoveTagSelect);

        bulkAddTagSelect.appendChild(el("option", { value: "" }, "Select tag..."));
        bulkRemoveTagSelect.appendChild(el("option", { value: "" }, "Select tag..."));

        for (const tag of currentCls.settings.tags) {
            bulkAddTagSelect.appendChild(el("option", { value: tag }, tag));
            bulkRemoveTagSelect.appendChild(el("option", { value: tag }, tag));
        }
    }

    bulkAddTagBtn.addEventListener("click", () => {
        const tag = bulkAddTagSelect.value;
        if (!tag) return;

        setState(ns => {
            const c = ns.classes.find(x => x.id === cls.id);
            if (!c) return;
            for (const student of c.students) {
                if (selectedStudentIds.has(student.id) && !student.tags.includes(tag)) {
                    student.tags.push(tag);
                }
            }
        });

        toast(`Added "${tag}" to ${selectedStudentIds.size} students`);
        renderStudentList();
    });

    bulkRemoveTagBtn.addEventListener("click", () => {
        const tag = bulkRemoveTagSelect.value;
        if (!tag) return;

        setState(ns => {
            const c = ns.classes.find(x => x.id === cls.id);
            if (!c) return;
            for (const student of c.students) {
                if (selectedStudentIds.has(student.id)) {
                    student.tags = student.tags.filter(t => t !== tag);
                }
            }
        });

        toast(`Removed "${tag}" from ${selectedStudentIds.size} students`);
        renderStudentList();
    });

    bulkSetSkillBtn.addEventListener("click", () => {
        const skill = Math.max(0, Math.min(10, parseInt(bulkSkillInput.value) || 5));

        setState(ns => {
            const c = ns.classes.find(x => x.id === cls.id);
            if (!c) return;
            for (const student of c.students) {
                if (selectedStudentIds.has(student.id)) {
                    student.skill = skill;
                }
            }
        });

        toast(`Set skill to ${skill} for ${selectedStudentIds.size} students`);
        renderStudentList();
    });

    deselectAllBtn.addEventListener("click", () => {
        selectedStudentIds.clear();
        updateBulkActionsBar();
        renderStudentList();
    });

    // Student list container
    const studentListContainer = el("div", {
        style: "flex: 1; overflow: auto; margin-bottom: 20px; border: 1px solid var(--border); border-radius: 8px; padding: 12px;"
    });

    function renderStudentList() {
        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === currentState.activeClassId);
        if (!currentCls) return;

        studentListContainer.innerHTML = "";
        updateBulkActionsBar();

        // Filter students by search term
        const filteredStudents = currentCls.students.filter(st =>
            st.name.toLowerCase().includes(searchTerm)
        );

        if (filteredStudents.length === 0) {
            const emptyMsg = searchTerm
                ? `No students match "${searchTerm}"`
                : "No students yet. Import some names above.";
            studentListContainer.appendChild(
                el("div", {
                    style: "color: var(--muted); text-align: center; padding: 20px;"
                }, emptyMsg)
            );
            return;
        }

        // Create table layout
        const table = el("div", {
            style: "display: flex; flex-direction: column; gap: 6px;"
        });

        // Header row with select all checkbox
        const headerRow = el("div", {
            style: "display: grid; grid-template-columns: 30px 40px 2fr 80px 3fr 80px 50px; gap: 12px; padding: 8px; font-weight: 600; font-size: 12px; color: var(--muted); border-bottom: 1px solid var(--border);"
        });

        const selectAllCheckbox = el("input", {
            type: "checkbox",
            title: "Select all"
        }) as HTMLInputElement;

        selectAllCheckbox.checked = filteredStudents.length > 0 &&
            filteredStudents.every(st => selectedStudentIds.has(st.id));

        selectAllCheckbox.addEventListener("change", () => {
            if (selectAllCheckbox.checked) {
                filteredStudents.forEach(st => selectedStudentIds.add(st.id));
            } else {
                filteredStudents.forEach(st => selectedStudentIds.delete(st.id));
            }
            updateBulkActionsBar();
            renderStudentList();
        });

        headerRow.append(
            selectAllCheckbox,
            el("span", {}, "Photo"),
            el("span", {}, "Name"),
            el("span", {}, "Skill"),
            el("span", {}, "Tags"),
            el("span", { style: "text-align: center;" }, "Absent"),
            el("span", {}, "")
        );
        table.appendChild(headerRow);

        // Student rows
        for (const st of filteredStudents) {
            const isSelected = selectedStudentIds.has(st.id);
            const row = el("div", {
                style: `display: grid; grid-template-columns: 30px 40px 2fr 80px 3fr 80px 50px; gap: 12px; align-items: center; padding: 8px; border: 1px solid var(--border); border-radius: 8px; background: ${isSelected ? 'var(--primary)' : 'var(--bg)'}; ${isSelected ? 'opacity: 0.9;' : ''}`
            });

            // Checkbox column
            const checkbox = el("input", {
                type: "checkbox",
                checked: isSelected ? true : undefined
            }) as HTMLInputElement;

            checkbox.addEventListener("change", () => {
                if (checkbox.checked) {
                    selectedStudentIds.add(st.id);
                } else {
                    selectedStudentIds.delete(st.id);
                }
                updateBulkActionsBar();
                renderStudentList();
            });

            // Avatar column: clickable avatar with upload
            const avatarCell = el("div", {
                style: "display: flex; align-items: center; justify-content: center; cursor: pointer;",
                title: "Click to upload photo"
            });

            const avatarEl = createAvatarElement(st, 32);
            avatarCell.appendChild(avatarEl);

            // Hidden file input for avatar upload
            const avatarInput = el("input", {
                type: "file",
                accept: "image/*",
                style: "display: none;"
            }) as HTMLInputElement;

            avatarInput.addEventListener("change", async () => {
                const file = avatarInput.files?.[0];
                if (!file) return;

                try {
                    const dataUrl = await processImageFile(file);
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === currentCls.id)!;
                        const student = c.students.find(x => x.id === st.id)!;
                        student.avatarUrl = dataUrl;
                    });
                    renderStudentList();
                    toast("Avatar updated");
                } catch (err) {
                    toast((err as Error).message || "Failed to upload image");
                }
            });

            avatarCell.addEventListener("click", () => avatarInput.click());
            avatarCell.appendChild(avatarInput);

            // Column 1: Name input
            const nameInput = el("input", {
                type: "text",
                value: st.name,
                placeholder: "Student name",
                style: `width: 100%; ${isSelected ? 'background: rgba(255,255,255,0.9);' : ''}`
            }) as HTMLInputElement;
            nameInput.addEventListener("input", () => {
                setState(ns => {
                    const c = ns.classes.find(x => x.id === currentCls.id)!;
                    const student = c.students.find(x => x.id === st.id)!;
                    student.name = nameInput.value;
                });
            });

            // Column 2: Skill input
            const skillInput = el("input", {
                type: "number",
                min: "0",
                max: "10",
                value: String(st.skill),
                style: `width: 100%; ${isSelected ? 'background: rgba(255,255,255,0.9);' : ''}`,
                title: "Skill level (0-10)"
            }) as HTMLInputElement;
            skillInput.addEventListener("input", () => {
                setState(ns => {
                    const c = ns.classes.find(x => x.id === currentCls.id)!;
                    const student = c.students.find(x => x.id === st.id)!;
                    student.skill = Number(skillInput.value) || 0;
                });
            });

            // Column 3: Multi-select tag dropdown
            const tagDropdown = createMultiSelectDropdown({
                availableTags: currentCls.settings.tags,
                selectedTags: st.tags,
                onChange: (newTags) => {
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === currentCls.id)!;
                        const student = c.students.find(x => x.id === st.id)!;
                        student.tags = newTags;
                    });
                },
                placeholder: "Select tags..."
            });

            // Column 4: Absent toggle
            const absentToggle = el("label", {
                class: "toggle-switch",
                style: "margin: 0 auto;"
            },
                el("input", { type: "checkbox", checked: st.absent ? true : undefined }),
                el("span", { class: "toggle-slider" })
            );
            const absentCheckbox = absentToggle.querySelector("input") as HTMLInputElement;
            absentCheckbox.addEventListener("change", () => {
                setState(ns => {
                    const c = ns.classes.find(x => x.id === currentCls.id)!;
                    const student = c.students.find(x => x.id === st.id)!;
                    student.absent = absentCheckbox.checked;
                });
            });

            // Column 5: Delete button
            const deleteBtn = el("button", {
                class: "danger",
                style: "width: 100%; padding: 6px; font-size: 11px;",
                title: "Remove student"
            }, "×");
            deleteBtn.addEventListener("click", () => {
                if (confirm(`Remove ${st.name}?`)) {
                    setState(ns => {
                        const c = ns.classes.find(x => x.id === currentCls.id)!;
                        c.students = c.students.filter(x => x.id !== st.id);
                    });
                    selectedStudentIds.delete(st.id);
                    renderStudentList();
                }
            });

            row.append(checkbox, avatarCell, nameInput, skillInput, tagDropdown, absentToggle, deleteBtn);
            table.appendChild(row);
        }

        studentListContainer.appendChild(table);
    }

    renderStudentList();

    // Close button
    const closeBtn = el("button", { class: "primary", style: "width: 100%;" }, "Close");
    closeBtn.addEventListener("click", () => document.body.removeChild(backdrop));

    dialog.append(
        title,
        importTA,
        importBtn,
        searchInput,
        bulkActionsBar,
        el("h3", { style: "margin: 0 0 12px 0; font-size: 16px;" }, "Students"),
        studentListContainer,
        closeBtn
    );

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) document.body.removeChild(backdrop);
    });

    // Don't auto-refresh on state updates to prevent focus loss during typing
    // Only refresh when students are added/removed
}
